/**
 * Skill Sheet Import Service
 * 
 * スキルシート（Excel/CSV）のインポート機能を提供するサービス
 * 主な機能:
 * - Excel/CSVファイルの読み込み
 * - スキルデータの抽出とパース
 * - データバリデーション
 * - エンジニア情報の自動生成
 * 
 * @service SkillSheetImportService
 */

import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { CreateEngineerRequest } from '../types';
import { AISkillSheetAnalyzer } from './aiSkillSheetAnalyzer';
import { EnhancedSingleEngineerAnalyzer } from './enhancedSingleEngineerAnalyzer';
import { ProjectExperienceCalculator } from './projectExperienceCalculator';

export interface SkillSheetData {
  name?: string;
  email?: string;
  phone?: string;
  age?: number;
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  nearest_station?: string;
  address?: string;
  education?: string;
  location?: string;
  experience_years?: number;
  hourly_rate?: number;
  availability_status?: 'available' | 'busy' | 'unavailable';
  remote_work_preference?: boolean;
  bio?: string;
  skills: {
    skill_name: string;
    skill_level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
    years_experience?: number;
  }[];
}

export interface ImportResult {
  success: boolean;
  data?: SkillSheetData[];
  errors: string[];
  warnings: string[];
  insights?: string[];
  suggestions?: string[];
  confidence?: number;
  aiAnalysis?: boolean;
  debugInfo?: any; // デバッグ情報
}

export interface ImportOptions {
  skipEmptyRows?: boolean;
  maxRows?: number;
  strictValidation?: boolean;
  useAI?: boolean;
  fallbackToAI?: boolean;
  singleEngineerMode?: boolean; // 新しいオプション
  calculateExperienceFromProjects?: boolean; // 案件から経験年数を計算
}

export class SkillSheetImportService {
  
  /**
   * ファイルをインポートして解析
   */
  static async importFile(file: File, options: ImportOptions = {}): Promise<ImportResult> {
    
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    try {
      let result: ImportResult;
      
      if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        result = await this.importExcel(file, options);
      } else if (fileExtension === 'csv') {
        result = await this.importCSV(file, options);
      } else {
        return {
          success: false,
          errors: ['サポートされていないファイル形式です。Excel(.xlsx/.xls)またはCSV(.csv)ファイルを使用してください。'],
          warnings: []
        };
      }
            
      // 単一エンジニアモードまたはAI解析の適用
      if (options.singleEngineerMode || options.useAI || (options.fallbackToAI && (!result.success || result.errors.length > 0))) {
        try {
          let aiResult: ImportResult;
          
          if (options.singleEngineerMode) {
            // 単一エンジニア専用の解析を使用
            aiResult = await this.importSingleEngineerWithAI(file, options);
          } else {
            // 従来の複数エンジニア対応AI解析
            aiResult = await this.importWithAI(file, options);
          }
          
          
          if (aiResult.success && aiResult.data && aiResult.data.length > 0) {
            return {
              ...aiResult,
              aiAnalysis: true,
              insights: aiResult.insights || [],
              suggestions: aiResult.suggestions || []
            };
          }
        } catch (aiError) {
          console.error('AI解析エラー:', aiError);
          // AI整形の失敗を警告として追加
          if (!result.warnings) {
            result.warnings = [];
          }
          result.warnings.push('AI整形に失敗したため、基本的な解析結果を使用しています');
        }
      }
      
      return result;
    } catch (error) {
      console.error('importFile全体でエラー:', error);
      return {
        success: false,
        errors: [`ファイル読み込み中にエラーが発生しました: ${(error as Error).message}`],
        warnings: []
      };
    }
  }
  
  /**
   * 単一エンジニア用AIインポート
   */
  static async importSingleEngineerWithAI(file: File, options: ImportOptions = {}): Promise<ImportResult> {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
        
    try {
      // ファイルを読み込み、生データを取得
      let rawData: any[][];
      
      if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        rawData = await this.readExcelToRawData(file);
      } else if (fileExtension === 'csv') {
        rawData = await this.readCSVToRawData(file);
      } else {
        return {
          success: false,
          errors: ['サポートされていないファイル形式です'],
          warnings: []
        };
      }
      
      // 強化版単一エンジニア専用AIで解析
      const singleResult = await EnhancedSingleEngineerAnalyzer.analyzeSingleEngineerSheet(rawData);
      
      if (singleResult.success && singleResult.data) {
        // 案件から経験年数を計算する場合
        if (options.calculateExperienceFromProjects) {
          try {
            const experienceResult = await ProjectExperienceCalculator.calculateFromExcelData(rawData);
            
            // 計算された技術経験をスキルとして統合
            if (Object.keys(experienceResult.technologies).length > 0) {
              const calculatedSkills = ProjectExperienceCalculator.convertToSkillFormat(experienceResult.technologies);
              
              
              // 既存のスキルと統合（改善されたマージロジック）
              const mergedSkills = this.mergeSkills(singleResult.data.skills, calculatedSkills);
              singleResult.data.skills = mergedSkills;
              
              // インサイトに追加
              const projectInsights = [
                ...experienceResult.insights,
                `総経験年数: ${experienceResult.totalExperienceYears}年`,
                `技術数: ${Object.keys(experienceResult.technologies).length}件`
              ];
              
              return {
                success: true,
                data: [singleResult.data],
                errors: [],
                warnings: experienceResult.warnings,
                insights: [...(singleResult.insights || []), ...projectInsights],
                suggestions: singleResult.suggestions,
                confidence: singleResult.confidence,
                aiAnalysis: true,
                debugInfo: {
                  ...singleResult.debugInfo,
                  experienceCalculation: experienceResult
                }
              };
            }
          } catch (expError) {
            console.error('案件経験年数計算エラー:', expError);
            // 計算に失敗しても元のデータは返す（警告は後で追加）
          }
        }
        
        return {
          success: true,
          data: [singleResult.data], // 単一エンジニアを配列に包む
          errors: [],
          warnings: [],
          insights: singleResult.insights,
          suggestions: singleResult.suggestions,
          confidence: singleResult.confidence,
          aiAnalysis: true,
          debugInfo: singleResult.debugInfo
        };
      } else {
        return {
          success: false,
          errors: ['単一エンジニアの情報抽出に失敗しました'],
          warnings: [],
          insights: singleResult.insights,
          suggestions: singleResult.suggestions,
          confidence: singleResult.confidence,
          aiAnalysis: true,
          debugInfo: singleResult.debugInfo
        };
      }
    } catch (error) {
      return {
        success: false,
        errors: [`単一エンジニアAI解析中にエラーが発生しました: ${(error as Error).message}`],
        warnings: [],
        aiAnalysis: true
      };
    }
  }
  
  /**
   * AIを使用したファイルインポート
   */
  static async importWithAI(file: File, options: ImportOptions = {}): Promise<ImportResult> {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    try {
      // ファイルを読み込み、生データを取得
      let rawData: any[][];
      
      if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        rawData = await this.readExcelToRawData(file);
      } else if (fileExtension === 'csv') {
        rawData = await this.readCSVToRawData(file);
      } else {
        return {
          success: false,
          errors: ['サポートされていないファイル形式です'],
          warnings: []
        };
      }
      
      // AIで解析
      const aiResult = await AISkillSheetAnalyzer.analyzeSheet(rawData);
      
      if (aiResult.success && aiResult.data) {
        // maxRowsの制限適用
        const maxRows = options.maxRows || 1000;
        const limitedData = aiResult.data.slice(0, maxRows);
        
        return {
          success: true,
          data: limitedData,
          errors: [],
          warnings: limitedData.length < aiResult.data.length ? 
            [`最大行数制限により${limitedData.length}件のみ表示しています`] : [],
          insights: aiResult.insights,
          suggestions: aiResult.suggestions,
          confidence: aiResult.confidence,
          aiAnalysis: true
        };
      } else {
        return {
          success: false,
          errors: ['AIによるデータ解析に失敗しました'],
          warnings: [],
          insights: aiResult.insights,
          suggestions: aiResult.suggestions,
          confidence: aiResult.confidence,
          aiAnalysis: true
        };
      }
    } catch (error) {
      return {
        success: false,
        errors: [`AI解析中にエラーが発生しました: ${(error as Error).message}`],
        warnings: [],
        aiAnalysis: true
      };
    }
  }
  
  /**
   * Excelファイルを生データとして読み込み
   */
  private static async readExcelToRawData(file: File): Promise<any[][]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          
          const workbook = XLSX.read(data, { type: 'array' });
          
          const sheetName = workbook.SheetNames[0];
          
          const worksheet = workbook.Sheets[sheetName];
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          
          resolve(jsonData);
        } catch (error) {
          console.error('readExcelToRawData内でエラー:', error);
          reject(error);
        }
      };
      
      reader.onerror = () => {
        console.error('FileReaderエラー');
        reject(new Error('ファイル読み込みエラー'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }
  
  /**
   * CSVファイルを生データとして読み込み
   */
  private static async readCSVToRawData(file: File): Promise<any[][]> {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        complete: (results) => {
          resolve(results.data as any[][]);
        },
        error: (error) => {
          reject(error);
        },
        encoding: 'UTF-8'
      });
    });
  }

  /**
   * Excelファイルの読み込み
   */
  private static async importExcel(file: File, options: ImportOptions): Promise<ImportResult> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // 最初のワークシートを使用
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // JSONに変換
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          const result = this.parseSheetData(jsonData, options);
          resolve(result);
        } catch (error) {
          resolve({
            success: false,
            errors: [`Excelファイルの読み込みに失敗しました: ${(error as Error).message}`],
            warnings: []
          });
        }
      };
      
      reader.onerror = () => {
        resolve({
          success: false,
          errors: ['ファイルの読み込みに失敗しました'],
          warnings: []
        });
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * CSVファイルの読み込み
   */
  private static async importCSV(file: File, options: ImportOptions): Promise<ImportResult> {
    return new Promise((resolve) => {
      Papa.parse(file, {
        complete: (results) => {
          try {
            const result = this.parseSheetData(results.data, options);
            resolve(result);
          } catch (error) {
            resolve({
              success: false,
              errors: [`CSVファイルの解析に失敗しました: ${(error as Error).message}`],
              warnings: []
            });
          }
        },
        error: (error) => {
          resolve({
            success: false,
            errors: [`CSVファイルの読み込みに失敗しました: ${error.message}`],
            warnings: []
          });
        },
        encoding: 'UTF-8'
      });
    });
  }

  /**
   * シートデータの解析
   */
  private static parseSheetData(rawData: any[], options: ImportOptions): ImportResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const parsedData: SkillSheetData[] = [];

    if (!rawData || rawData.length === 0) {
      return {
        success: false,
        errors: ['ファイルにデータが含まれていません'],
        warnings: []
      };
    }

    // ヘッダー行の解析
    const headers = rawData[0] as string[];
    const headerMap = this.createHeaderMap(headers);

    if (Object.keys(headerMap).length === 0) {
      return {
        success: false,
        errors: ['認識可能なヘッダーが見つかりません。適切なスキルシートフォーマットを使用してください。'],
        warnings: []
      };
    }

    // データ行の解析
    const dataRows = rawData.slice(1);
    const maxRows = options.maxRows || 1000;
    const rowsToProcess = dataRows.slice(0, maxRows);

    rowsToProcess.forEach((row: any[], rowIndex: number) => {
      if (options.skipEmptyRows && this.isEmptyRow(row)) {
        return;
      }

      try {
        const engineerData = this.parseRowData(row, headerMap, rowIndex + 2); // +2 for header + 0-based index
        
        if (engineerData.errors.length > 0) {
          errors.push(...engineerData.errors);
        }
        
        if (engineerData.warnings.length > 0) {
          warnings.push(...engineerData.warnings);
        }

        if (engineerData.data) {
          parsedData.push(engineerData.data);
        }
      } catch (error) {
        errors.push(`${rowIndex + 2}行目: 解析エラー - ${(error as Error).message}`);
      }
    });

    return {
      success: parsedData.length > 0,
      data: parsedData,
      errors,
      warnings
    };
  }

  /**
   * ヘッダーマッピングの作成
   */
  private static createHeaderMap(headers: string[]): { [key: string]: number } {
    const map: { [key: string]: number } = {};
    
    // 日本語と英語のヘッダーパターン
    const patterns = {
      name: ['名前', '氏名', 'name', 'full_name', 'fullname'],
      email: ['メール', 'メールアドレス', 'email', 'mail'],
      phone: ['電話', '電話番号', 'phone', 'tel', 'telephone'],
      location: ['住所', '勤務地', '所在地', 'location', 'address'],
      experience_years: ['経験年数', '経験', '年数', 'experience', 'years', 'experience_years'],
      hourly_rate: ['時給', '単価', '時間単価', 'rate', 'hourly_rate', 'hourly'],
      availability_status: ['状況', 'ステータス', '稼働状況', 'status', 'availability'],
      remote_work_preference: ['リモート', 'リモートワーク', 'remote', 'remote_work'],
      bio: ['自己紹介', '経歴', 'プロフィール', 'bio', 'profile', 'description'],
      // スキル関連（複数のパターンを想定）
      skill_name: ['スキル', 'スキル名', 'skill', 'skill_name'],
      skill_level: ['レベル', 'スキルレベル', 'level', 'skill_level'],
      skill_years: ['スキル経験年数', 'スキル年数', 'skill_years', 'years_experience']
    };

    headers.forEach((header, index) => {
      if (!header) return;
      
      const normalizedHeader = header.toString().toLowerCase().trim();
      
      Object.entries(patterns).forEach(([key, patterns]) => {
        patterns.forEach(pattern => {
          if (normalizedHeader.includes(pattern.toLowerCase()) && !map[key]) {
            map[key] = index;
          }
        });
      });
    });

    return map;
  }

  /**
   * 行データの解析
   */
  private static parseRowData(
    row: any[], 
    headerMap: { [key: string]: number }, 
    rowNumber: number
  ): { data?: SkillSheetData; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 基本情報の抽出
    const name = this.getCellValue(row, headerMap.name);
    if (!name || name.trim() === '') {
      errors.push(`${rowNumber}行目: 名前が入力されていません`);
      return { errors, warnings };
    }

    const email = this.getCellValue(row, headerMap.email);
    const phone = this.getCellValue(row, headerMap.phone);
    const location = this.getCellValue(row, headerMap.location);
    const bio = this.getCellValue(row, headerMap.bio);

    // 数値データの解析
    const experience_years = this.parseNumber(this.getCellValue(row, headerMap.experience_years));
    const hourly_rate = this.parseNumber(this.getCellValue(row, headerMap.hourly_rate));

    // ステータスの解析
    const availability_status = this.parseAvailabilityStatus(
      this.getCellValue(row, headerMap.availability_status)
    );

    // リモートワーク設定の解析
    const remote_work_preference = this.parseBoolean(
      this.getCellValue(row, headerMap.remote_work_preference)
    );

    // スキルデータの抽出（複数の方法で試行）
    const skills = this.extractSkills(row, headerMap, rowNumber);

    // バリデーション
    if (email && !this.isValidEmail(email)) {
      warnings.push(`${rowNumber}行目: メールアドレスの形式が正しくありません`);
    }

    if (experience_years !== undefined && (experience_years < 0 || experience_years > 50)) {
      warnings.push(`${rowNumber}行目: 経験年数は0～50年の範囲で入力してください`);
    }

    const engineerData: SkillSheetData = {
      name: name.trim(),
      email: email || undefined,
      phone: phone || undefined,
      location: location || undefined,
      experience_years,
      hourly_rate,
      availability_status,
      remote_work_preference,
      bio: bio || undefined,
      skills
    };

    return { data: engineerData, errors, warnings };
  }

  /**
   * セル値の取得
   */
  private static getCellValue(row: any[], columnIndex: number | undefined): string {
    if (columnIndex === undefined || columnIndex >= row.length) {
      return '';
    }
    return row[columnIndex]?.toString()?.trim() || '';
  }

  /**
   * 数値の解析（小数点数を整数に変換）
   */
  private static parseNumber(value: string): number | undefined {
    if (!value || value === '') return undefined;
    const num = parseFloat(value.replace(/[^\d.-]/g, ''));
    if (isNaN(num)) return undefined;
    
    // 小数点数を四捨五入して整数に変換
    const rounded = Math.round(num);
    return rounded;
  }

  /**
   * 稼働ステータスの解析
   */
  private static parseAvailabilityStatus(value: string): 'available' | 'busy' | 'unavailable' {
    if (!value) return 'available';
    
    const normalized = value.toLowerCase().trim();
    if (normalized.includes('稼働中') || normalized.includes('busy') || normalized.includes('occupied')) {
      return 'busy';
    } else if (normalized.includes('稼働不可') || normalized.includes('unavailable') || normalized.includes('not available')) {
      return 'unavailable';
    }
    return 'available';
  }

  /**
   * ブール値の解析
   */
  private static parseBoolean(value: string): boolean {
    if (!value) return false;
    const normalized = value.toLowerCase().trim();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || 
           normalized === 'はい' || normalized === '○' || normalized === '可';
  }

  /**
   * スキルの抽出
   */
  private static extractSkills(
    row: any[], 
    headerMap: { [key: string]: number }, 
    rowNumber: number
  ): SkillSheetData['skills'] {
    const skills: SkillSheetData['skills'] = [];

    // 単一スキル列からの抽出
    const skillName = this.getCellValue(row, headerMap.skill_name);
    const skillLevel = this.parseSkillLevel(this.getCellValue(row, headerMap.skill_level));
    const skillYears = this.parseNumber(this.getCellValue(row, headerMap.skill_years));

    if (skillName) {
      skills.push({
        skill_name: skillName,
        skill_level: skillLevel,
        years_experience: skillYears
      });
    }

    // 複数スキル列（スキル1、スキル2等）からの抽出
    for (let i = 1; i <= 10; i++) {
      const skillColumnName = row.find((cell: any, index: number) => {
        const header = headerMap[`skill_${i}`] || 
                      row.findIndex((h: any) => h?.toString().toLowerCase().includes(`スキル${i}`) || 
                                                h?.toString().toLowerCase().includes(`skill${i}`));
        return index === header;
      });

      if (skillColumnName) {
        const skillName = skillColumnName.toString().trim();
        if (skillName && !skills.some(s => s.skill_name === skillName)) {
          skills.push({
            skill_name: skillName,
            skill_level: 'intermediate',
            years_experience: 1
          });
        }
      }
    }

    // カンマ区切りスキルの処理
    if (skillName && skillName.includes(',')) {
      const skillList = skillName.split(',');
      skills.length = 0; // Clear previous single skill
      
      skillList.forEach(skill => {
        const trimmedSkill = skill.trim();
        if (trimmedSkill) {
          skills.push({
            skill_name: trimmedSkill,
            skill_level: skillLevel,
            years_experience: skillYears
          });
        }
      });
    }

    return skills;
  }

  /**
   * スキルレベルの解析
   */
  private static parseSkillLevel(value: string): 'beginner' | 'intermediate' | 'advanced' | 'expert' {
    if (!value) return 'intermediate';
    
    const normalized = value.toLowerCase().trim();
    if (normalized.includes('初級') || normalized.includes('beginner') || normalized === '1') {
      return 'beginner';
    } else if (normalized.includes('上級') || normalized.includes('advanced') || normalized === '3') {
      return 'advanced';
    } else if (normalized.includes('エキスパート') || normalized.includes('expert') || normalized === '4') {
      return 'expert';
    }
    return 'intermediate';
  }

  /**
   * 空行判定
   */
  private static isEmptyRow(row: any[]): boolean {
    return row.every(cell => !cell || cell.toString().trim() === '');
  }

  /**
   * メールアドレスの妥当性チェック
   */
  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * 文字列の長さをチェックして切り詰める
   */
  private static truncateString(value: string | undefined, maxLength: number, fieldName: string = ''): string | undefined {
    if (!value) return undefined;
    
    const trimmedValue = value.trim();
    if (trimmedValue.length <= maxLength) {
      return trimmedValue;
    }
    
    console.warn(`${fieldName}フィールドが最大長${maxLength}を超えています（${trimmedValue.length}文字）。切り詰めます。`);
    return trimmedValue.substring(0, maxLength);
  }

  /**
   * CreateEngineerRequestへの変換
   */
  static convertToEngineerRequest(skillSheetData: SkillSheetData): CreateEngineerRequest {
    
    // スキルデータの正規化
    const normalizedSkills: {
      skill_name: string;
      skill_level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
      years_experience?: number;
    }[] = [];
    
    if (skillSheetData.skills) {
      for (const skill of skillSheetData.skills) {
        // skill_nameのバリデーション
        if (!skill.skill_name || skill.skill_name.trim() === '') {
          console.warn('空のスキル名をスキップ:', skill);
          continue;
        }
        
        const trimmedSkillName = skill.skill_name.trim();
        if (trimmedSkillName.length > 100) {
          console.warn('スキル名が長すぎるためスキップ:', trimmedSkillName);
          continue;
        }
        
        // スキルレベルの検証
        const validLevels: ('beginner' | 'intermediate' | 'advanced' | 'expert')[] = 
          ['beginner', 'intermediate', 'advanced', 'expert'];
        
        const skillLevel = validLevels.includes(skill.skill_level) 
          ? skill.skill_level 
          : 'beginner';
        
        // 経験年数の検証（小数点数を整数に変換）
        let yearsExperience: number | undefined = undefined;
        if (skill.years_experience !== undefined && 
            skill.years_experience !== null && 
            typeof skill.years_experience === 'number' &&
            skill.years_experience >= 0 && 
            skill.years_experience <= 50) {
          // 小数点数を四捨五入して整数に変換
          yearsExperience = Math.round(skill.years_experience);
          
        } else if (skill.years_experience !== undefined) {
          const originalValue = skill.years_experience;
          console.warn(`スキル "${skill.skill_name}" の経験年数が無効です (${originalValue})。0年に設定されました。`);
          yearsExperience = 0;
        }
        
        const normalizedSkill = {
          skill_name: trimmedSkillName,
          skill_level: skillLevel,
          years_experience: yearsExperience
        };
        
        normalizedSkills.push(normalizedSkill);
      }
    }
    
    // 名前の検証
    const engineerName = skillSheetData.name?.trim();
    if (!engineerName || engineerName === '') {
      throw new Error('エンジニア名が必要です');
    }
    
    const engineerRequest = {
      name: this.truncateString(engineerName, 100, '名前') || engineerName, // 名前は必須なので最悪元の値
      email: this.truncateString(skillSheetData.email, 255, 'メールアドレス'),
      phone: this.truncateString(skillSheetData.phone, 20, '電話番号'),
      // age: (skillSheetData.age !== undefined && 
      //       skillSheetData.age >= 18 && 
      //       skillSheetData.age <= 100) 
      //   ? skillSheetData.age : undefined,
      // gender: skillSheetData.gender,
      // nearest_station: this.truncateString(skillSheetData.nearest_station, 100, '最寄駅'),
      // address: this.truncateString(skillSheetData.address, 255, '住所'), // 一時的にコメントアウト
      // education: this.truncateString(skillSheetData.education, 255, '最終学歴'),
      location: this.truncateString(skillSheetData.location, 255, '住所'),
      experience_years: (skillSheetData.experience_years !== undefined && 
                        skillSheetData.experience_years >= 0 && 
                        skillSheetData.experience_years <= 50) 
        ? skillSheetData.experience_years : undefined,
      hourly_rate: (skillSheetData.hourly_rate !== undefined && 
                   skillSheetData.hourly_rate >= 500 && 
                   skillSheetData.hourly_rate <= 50000) 
        ? skillSheetData.hourly_rate : undefined,
      availability_status: skillSheetData.availability_status || 'available',
      remote_work_preference: skillSheetData.remote_work_preference || false,
      bio: this.truncateString(skillSheetData.bio, 1000, '自己紹介'), // bioはTEXT型だが安全のため制限
      skills: normalizedSkills
    };
        
    return engineerRequest;
  }

  /**
   * スキルをマージ（案件計算優先、AI補完方式）
   * 案件計算で抽出された技術を最優先とし、AI抽出は閾値を満たすもののみ統合
   * 閾値: 信頼度0.7以上、経験年数0ヶ月以上
   */
  private static mergeSkills(
    existingSkills: SkillSheetData['skills'], 
    calculatedSkills: { skill_name: string; skill_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'; years_experience: number }[]
  ): SkillSheetData['skills'] {
    const merged: SkillSheetData['skills'] = [];
    const processed = new Set<string>();
    
    // 設定値（実用的な調整）
    const MIN_CONFIDENCE_THRESHOLD = 0.7; // AI抽出の最小信頼度
    const MIN_EXPERIENCE_THRESHOLD = 0;  // 最小経験年数（0ヶ月 = 制限なし）
    const MIN_PROJECT_EVIDENCE = 0;      // 案件計算の最小経験（0ヶ月 = 制限なし）

    // 最優先: 案件計算のスキル（実績ベースなので高信頼度）
    for (const calcSkill of calculatedSkills) {
      // 案件計算でも最小経験年数を満たすもののみ採用
      if (calcSkill.years_experience < MIN_PROJECT_EVIDENCE) {
        continue;
      }
      
      const skillNameLower = calcSkill.skill_name.toLowerCase();
      
      // AIからスキルレベル情報を取得（あれば）
      const aiSkill = existingSkills.find(es => es.skill_name.toLowerCase() === skillNameLower);
      
      // 技術名の妥当性をチェック
      if (!this.isValidTechnology(calcSkill.skill_name)) {
        continue;
      }
      
      // 案件計算を最優先とするスキル
      const finalSkill = {
        skill_name: aiSkill?.skill_name || calcSkill.skill_name, // 表記はAI優先（なければ案件の表記）
        skill_level: aiSkill?.skill_level || calcSkill.skill_level, // レベルはAI優先（なければ案件レベル）
        years_experience: Math.round(calcSkill.years_experience * 10) / 10 // 案件計算の年数（小数点1位まで）
      };
      
      merged.push(finalSkill);
      processed.add(skillNameLower);
    }

    // 補完: AI抽出でのみ発見された技術（厳格な条件で追加）
    for (const aiSkill of existingSkills) {
      const skillNameLower = aiSkill.skill_name.toLowerCase();
      if (!processed.has(skillNameLower)) {
        
        // 技術名の妥当性をチェック
        if (!this.isValidTechnology(aiSkill.skill_name)) {
          continue;
        }
        
        // 経験年数が最小閾値を満たすかチェック
        const experienceYears = aiSkill.years_experience || 0;
        if (experienceYears < MIN_EXPERIENCE_THRESHOLD) {
          continue;
        }
        
        // AI信頼度をスキル名から推定（簡易版）
        const estimatedConfidence = this.estimateSkillConfidence(aiSkill.skill_name, aiSkill);
        if (estimatedConfidence < MIN_CONFIDENCE_THRESHOLD) {
          continue;
        }
        
        // 高信頼度のAI抽出スキルのみ追加
        const finalSkill = {
          skill_name: aiSkill.skill_name,
          skill_level: aiSkill.skill_level,
          years_experience: Math.max(experienceYears, 0.2) // 最小値を設定
        };
        
        merged.push(finalSkill);
      }
    }
    
    // 最終結果をソートして返す（経験年数降順）
    const sortedMerged = merged.sort((a, b) => (b.years_experience || 0) - (a.years_experience || 0));

    return sortedMerged;
  }

  /**
   * 技術名の妥当性をチェック
   */
  private static isValidTechnology(skillName: string): boolean {
    if (!skillName || skillName.trim().length < 2) {
      return false;
    }
    
    const invalidPatterns = [
      // 一般的すぎる単語
      /^(開発|設計|実装|運用|保守|テスト|検証)$/i,
      /^(管理|企画|調査|分析|改善|対応)$/i,
      /^(作成|制作|構築|導入|展開|配布)$/i,

      // 業務内容（技術ではない）
      /^(要件定義|基本設計|詳細設計|単体テスト|結合テスト)$/i,
      /^(進捗管理|品質管理|工程管理|プロジェクト管理)$/i,

      // 曖昧な表現
      /^(その他|等|など|系|関連|ベース)$/i,
      /^(フロントエンド|バックエンド|インフラ|データベース)$/i,
    ];
    
    return !invalidPatterns.some(pattern => pattern.test(skillName.trim()));
  }

  /**
   * スキル信頼度の推定（簡易版）
   */
  private static estimateSkillConfidence(skillName: string, skill: any): number {
    let confidence = 0.5; // ベース信頼度
    
    // 経験年数がある場合
    if (skill.years_experience && skill.years_experience > 0) {
      confidence += 0.3;
    }
    
    // 明確なスキルレベルがある場合
    if (skill.skill_level && skill.skill_level !== 'beginner') {
      confidence += 0.2;
    }
    
    // 一般的な技術名の場合（信頼度上昇）
    const popularTechnologies = [
      'JavaScript', 'TypeScript', 'React', 'Vue.js', 'Angular', 'Node.js', 'Python', 'Java',
      'PHP', 'MySQL', 'PostgreSQL', 'AWS', 'Docker', 'Git', 'HTML', 'CSS'
    ];
    
    if (popularTechnologies.some(tech => tech.toLowerCase() === skillName.toLowerCase())) {
      confidence += 0.2;
    }
    
    return Math.max(0, Math.min(1, confidence)); // 0-1の範囲に制限
  }

}

export default SkillSheetImportService;